import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, Download, ArrowLeft, Compass, Check, X } from "lucide-react";
import slides from "../data/satMasterSlides";
import Hummingbird from "../components/brand/Hummingbird";

const COLORS = { gold: "#D4AF37", goldSoft: "#EAD08A", violet: "#a48cff", midnight: "#081328" };

// ── Slide layout templates ────────────────────────────────────────────────
const SlideCover = ({ s }) => (
  <div className="h-full w-full flex flex-col items-center justify-center text-center px-8 lg:px-16 relative">
    <div className="absolute top-10 right-10 opacity-30 hummingbird-float pointer-events-none">
      <Hummingbird size={140} />
    </div>
    {s.kicker && <div className="overline mb-6" style={{ color: COLORS.gold, letterSpacing: "0.5em" }}>{s.kicker}</div>}
    <h1 className="serif font-light tracking-tight leading-[0.95]"
        style={{ fontSize: "clamp(2.5rem, 7vw, 7rem)", color: COLORS.gold }}>
      {s.title}
    </h1>
    {s.subtitle && (
      <p className="serif italic mt-8 text-xl md:text-2xl lg:text-3xl text-white/75 max-w-3xl leading-snug">
        {s.subtitle}
      </p>
    )}
    {s.tagline && (
      <div className="mt-12 text-[10px] md:text-xs tracking-[0.5em] text-white/55 uppercase font-medium">
        {s.tagline}
      </div>
    )}
    <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-3 text-white/30 text-[10px] tracking-[0.4em] uppercase">
      <Compass size={12} /> SAT Master · Schweizer Akademie für Transformation
    </div>
  </div>
);

const SlideContent = ({ s }) => (
  <div className="h-full w-full grid lg:grid-cols-[1fr_1fr] gap-10 lg:gap-16 px-8 lg:px-16 py-12 lg:py-16 overflow-y-auto">
    {/* Left: bullets */}
    <div>
      {s.kicker && <div className="overline mb-3" style={{ color: COLORS.gold }}>{s.kicker}</div>}
      <h2 className="serif text-3xl md:text-4xl lg:text-5xl font-light leading-tight mb-3"
          style={{ color: COLORS.gold }}>
        {s.title}
      </h2>
      {s.subtitle && <p className="serif italic text-white/65 text-lg mb-8">{s.subtitle}</p>}
      <ul className="space-y-5">
        {s.bullets?.map((b, i) => (
          <li key={i} className="flex gap-4">
            <div className="shrink-0 w-8 h-8 rounded-full border flex items-center justify-center text-xs font-semibold mt-0.5"
                 style={{ borderColor: COLORS.gold, color: COLORS.gold }}>
              {String(i + 1).padStart(2, "0")}
            </div>
            <div>
              <div className="font-semibold tracking-wide uppercase text-sm" style={{ color: COLORS.gold }}>{b.h}</div>
              {b.t && <div className="text-white/70 text-sm md:text-[15px] leading-relaxed mt-1">{b.t}</div>}
            </div>
          </li>
        ))}
      </ul>
    </div>

    {/* Right: quote + side panel */}
    <div className="flex flex-col gap-6 justify-center">
      {s.mantra && (
        <div className="relative rounded-3xl p-8 lg:p-10 border" style={{ borderColor: `${COLORS.gold}55`, background: "rgba(212,175,55,0.04)" }}>
          <div className="text-3xl absolute top-3 left-5" style={{ color: COLORS.gold }}>&ldquo;</div>
          <p className="serif italic text-xl md:text-2xl text-white/85 leading-snug">{s.mantra}</p>
          {s.mantraSub && <p className="text-white/55 text-sm mt-4 italic">{s.mantraSub}</p>}
        </div>
      )}
      {s.side && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-7">
          <div className="text-xs uppercase tracking-[0.3em] font-semibold mb-5" style={{ color: COLORS.gold }}>
            {s.side.title}
          </div>
          <ul className="space-y-3">
            {s.side.items.map((it, i) => (
              <li key={i} className="text-white/75 text-sm flex gap-3">
                <span style={{ color: COLORS.gold }}>◆</span>
                {it}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  </div>
);

const SlideComparison = ({ s }) => (
  <div className="h-full w-full px-8 lg:px-16 py-12 lg:py-16 overflow-y-auto">
    {s.kicker && <div className="overline" style={{ color: COLORS.gold }}>{s.kicker}</div>}
    <h2 className="serif text-3xl md:text-5xl font-light mt-2 mb-2" style={{ color: COLORS.gold }}>{s.title}</h2>
    {s.subtitle && <p className="serif italic text-white/65 text-lg mb-8">{s.subtitle}</p>}

    <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
      <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-6 lg:p-8">
        <div className="text-red-300 text-xs uppercase tracking-[0.4em] font-bold mb-5 flex items-center gap-2">
          <X size={14} /> VORHER
        </div>
        <ul className="space-y-4">
          {s.vorher.map((it, i) => (
            <li key={i} className="flex gap-3 text-white/80">
              <X size={16} className="text-red-400 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-sm" style={{ color: "#fca5a5" }}>{it.h}</div>
                {it.t && <div className="text-white/55 text-sm mt-0.5">{it.t}</div>}
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-2xl border p-6 lg:p-8" style={{ borderColor: `${COLORS.gold}55`, background: "rgba(212,175,55,0.05)" }}>
        <div className="text-xs uppercase tracking-[0.4em] font-bold mb-5 flex items-center gap-2" style={{ color: "#48d597" }}>
          <Check size={14} /> NACHHER
        </div>
        <ul className="space-y-4">
          {s.nachher.map((it, i) => (
            <li key={i} className="flex gap-3 text-white/85">
              <Check size={16} className="shrink-0 mt-0.5" style={{ color: "#48d597" }} />
              <div>
                <div className="font-semibold text-sm" style={{ color: "#86efac" }}>{it.h}</div>
                {it.t && <div className="text-white/60 text-sm mt-0.5">{it.t}</div>}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
    {s.mantra && (
      <div className="mt-8 text-center max-w-3xl mx-auto">
        <p className="serif italic text-lg md:text-xl text-white/80">&ldquo;{s.mantra}&rdquo;</p>
      </div>
    )}
  </div>
);

const SlideCases = ({ s }) => (
  <div className="h-full w-full px-6 lg:px-12 py-10 overflow-y-auto">
    <div className="text-center mb-8">
      <div className="overline" style={{ color: COLORS.gold }}>{s.kicker}</div>
      <h2 className="serif text-3xl md:text-5xl font-light mt-2" style={{ color: COLORS.gold }}>{s.title}</h2>
      <p className="serif italic text-white/65 text-lg mt-3">{s.subtitle}</p>
    </div>
    <div className="space-y-6 max-w-6xl mx-auto">
      {s.cases.map((c, i) => (
        <div key={i} className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 grid md:grid-cols-[180px_1fr_1fr_1fr] gap-5">
          <div>
            <div className="serif text-2xl" style={{ color: COLORS.gold }}>{c.name}</div>
            <div className="text-white/55 text-xs mt-1">{c.age}</div>
            <p className="serif italic text-white/75 text-sm mt-3">&ldquo;{c.quote}&rdquo;</p>
          </div>
          <div>
            <div className="text-red-300 text-[10px] uppercase tracking-[0.3em] font-bold mb-2">Vorher</div>
            <ul className="space-y-1.5 text-white/65 text-xs">
              {c.before.map((b, j) => <li key={j} className="flex gap-2"><X size={10} className="text-red-400 mt-1 shrink-0" /> {b}</li>)}
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-2" style={{ color: COLORS.gold }}>Im SAT Master</div>
            <ul className="space-y-1.5 text-white/65 text-xs">
              {c.process.map((b, j) => <li key={j} className="flex gap-2"><Compass size={10} style={{ color: COLORS.gold }} className="mt-1 shrink-0" /> {b}</li>)}
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-2" style={{ color: "#48d597" }}>Nachher</div>
            <ul className="space-y-1.5 text-white/70 text-xs">
              {c.after.map((b, j) => <li key={j} className="flex gap-2"><Check size={10} style={{ color: "#48d597" }} className="mt-1 shrink-0" /> {b}</li>)}
            </ul>
          </div>
        </div>
      ))}
    </div>
    {s.mantra && (
      <p className="serif italic text-center text-white/70 mt-8 max-w-2xl mx-auto">&ldquo;{s.mantra}&rdquo;</p>
    )}
  </div>
);

const SlideFinale = ({ s }) => (
  <div className="h-full w-full grid lg:grid-cols-[1fr_1fr] gap-12 px-8 lg:px-16 py-12 overflow-y-auto">
    <div>
      {s.kicker && <div className="overline" style={{ color: COLORS.gold }}>{s.kicker}</div>}
      <h2 className="serif text-4xl md:text-5xl lg:text-6xl font-light mt-2" style={{ color: COLORS.gold }}>{s.title}</h2>
      <p className="serif italic text-white/75 text-lg mt-4">{s.subtitle}</p>
      <ul className="space-y-4 mt-8">
        {s.bullets?.map((b, i) => (
          <li key={i}>
            <div className="font-semibold uppercase text-xs tracking-wider" style={{ color: COLORS.gold }}>{b.h}</div>
            <div className="text-white/70 text-sm mt-1 leading-relaxed">{b.t}</div>
          </li>
        ))}
      </ul>
    </div>
    <div className="flex flex-col gap-6 justify-center">
      {s.formula && (
        <div className="rounded-2xl border p-7" style={{ borderColor: `${COLORS.gold}55`, background: "rgba(212,175,55,0.05)" }}>
          <div className="text-xs uppercase tracking-[0.3em] font-bold mb-4" style={{ color: COLORS.gold }}>
            Die Formel deiner Transformation
          </div>
          <div className="space-y-3">
            {s.formula.map((f, i) => (
              <div key={i} className="text-xl serif" style={{ color: i === s.formula.length - 1 ? COLORS.gold : "rgba(255,255,255,0.85)" }}>
                {f}
              </div>
            ))}
          </div>
        </div>
      )}
      {s.mantra && (
        <div className="relative rounded-2xl p-8 border" style={{ borderColor: `${COLORS.gold}55`, background: "rgba(212,175,55,0.04)" }}>
          <p className="serif italic text-xl text-white/90 leading-snug">&ldquo;{s.mantra}&rdquo;</p>
          {s.mantraSub && <p className="text-sm mt-4 font-semibold tracking-wide" style={{ color: COLORS.gold }}>{s.mantraSub}</p>}
        </div>
      )}
    </div>
  </div>
);

// ── Main Deck ─────────────────────────────────────────────────────────────
const renderSlide = (s) => {
  switch (s.type) {
    case "cover": return <SlideCover s={s} />;
    case "comparison": return <SlideComparison s={s} />;
    case "cases": return <SlideCases s={s} />;
    case "finale": return <SlideFinale s={s} />;
    case "pricing": return <SlideComparison s={s} />;
    case "content":
    case "process":
    case "funnel":
    case "formula":
    default: return <SlideContent s={s} />;
  }
};

export default function SatMasterDeck() {
  const [idx, setIdx] = useState(0);
  const [fs, setFs] = useState(false);
  const total = slides.length;

  const go = useCallback((delta) => {
    setIdx((i) => Math.min(total - 1, Math.max(0, i + delta)));
  }, [total]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "f") setFs((f) => !f);
      else if (e.key === "Escape") setFs(false);
      else if (e.key === "Home") setIdx(0);
      else if (e.key === "End") setIdx(total - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, total]);

  const slide = slides[idx];

  return (
    <div className={`${fs ? "fixed inset-0 z-[100]" : "min-h-screen pt-24 pb-12"} bg-[#0E1A30] text-white`} data-testid="sat-master-deck">
      <div className={`${fs ? "h-screen" : ""} container-sat`}>
        {/* Top bar */}
        {!fs && (
          <div className="flex items-center justify-between mb-6">
            <Link to="/sat-master" className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase hover:translate-x-[-2px] transition" style={{ color: COLORS.gold }} data-testid="deck-back">
              <ArrowLeft size={12} /> Zurück
            </Link>
            <div className="text-[10px] tracking-[0.35em] uppercase text-white/45">
              SAT Master · Programm-Übersicht
            </div>
            <button onClick={() => setFs(true)} className="text-white/55 hover:text-[#D4AF37] flex items-center gap-2 text-xs uppercase tracking-wider" data-testid="deck-fullscreen-on">
              <Maximize2 size={14} /> Vollbild
            </button>
          </div>
        )}

        {/* Slide canvas */}
        <div className={`relative ${fs ? "h-full" : "h-[78vh] min-h-[640px]"} rounded-2xl overflow-hidden`}
             style={{ background: "linear-gradient(135deg, #0E1A30 0%, #050B1B 100%)", border: fs ? "none" : "1px solid rgba(212,175,55,0.18)" }}
             data-testid={`slide-${slide.num}`}>
          {/* SAT Brand corner */}
          <div className="absolute top-5 left-6 flex items-center gap-3 z-10">
            <div className="w-8 h-8 rounded-full border flex items-center justify-center" style={{ borderColor: COLORS.gold }}>
              <Compass size={14} style={{ color: COLORS.gold }} />
            </div>
            <div className="leading-tight">
              <div className="serif text-sm" style={{ color: COLORS.gold }}>SAT</div>
              <div className="text-[8px] tracking-[0.3em] uppercase text-white/50">Schweizer Akademie</div>
            </div>
          </div>
          {/* Page number */}
          <div className="absolute top-5 right-6 text-xs tracking-[0.3em] uppercase font-light z-10" style={{ color: COLORS.gold }}>
            {String(slide.num).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </div>

          {/* Slide content */}
          <div className="h-full pt-16 pb-12">
            {renderSlide(slide)}
          </div>

          {/* Footer signature */}
          <div className="absolute bottom-3 left-0 right-0 text-center text-[9px] tracking-[0.35em] uppercase text-white/35">
            SAT — Schweizer Akademie für Transformation · Nenad Vladovic
          </div>

          {/* Fullscreen close */}
          {fs && (
            <button onClick={() => setFs(false)} className="absolute top-5 right-1/2 translate-x-1/2 text-white/55 hover:text-[#D4AF37] flex items-center gap-2 text-xs uppercase tracking-wider z-20" data-testid="deck-fullscreen-off">
              <Minimize2 size={14} /> Schließen (Esc)
            </button>
          )}
        </div>

        {/* Navigation */}
        <div className="mt-5 flex items-center justify-between">
          <button onClick={() => go(-1)} disabled={idx === 0}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition disabled:opacity-30 disabled:hover:border-white/15 disabled:hover:text-white"
                  data-testid="deck-prev">
            <ChevronLeft size={16} /> Vorherige
          </button>

          {/* Progress dots */}
          <div className="hidden md:flex items-center gap-1.5 max-w-2xl overflow-x-auto">
            {slides.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)}
                      className={`shrink-0 transition-all rounded-full ${i === idx ? "w-6 h-2 bg-[#D4AF37]" : "w-2 h-2 bg-white/20 hover:bg-white/40"}`}
                      aria-label={`Slide ${i + 1}`}
                      data-testid={`deck-dot-${i}`} />
            ))}
          </div>

          <button onClick={() => go(1)} disabled={idx === total - 1}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition disabled:opacity-30"
                  style={{ background: COLORS.gold, color: COLORS.midnight }}
                  data-testid="deck-next">
            Nächste <ChevronRight size={16} />
          </button>
        </div>

        {!fs && (
          <div className="mt-6 text-center text-[10px] tracking-[0.3em] uppercase text-white/30">
            Pfeiltasten ◄ ► · Leertaste · F für Vollbild · Esc zum Schließen
          </div>
        )}
      </div>
    </div>
  );
}
